import { useEffect, useState, useCallback } from 'react';
import {
    Plus, Pencil, Loader2, GripVertical, RotateCcw,
    Building2, ToggleLeft, ToggleRight, CheckCircle2, XCircle, Sparkles,
} from 'lucide-react';
import { TagInput } from '@/components/ui/TagInput';
import { useAuth } from '@/context/AuthContext';
import * as apiClient from '@/services/apiClient';
import { toast } from 'sonner';
import PageHeader from '@/components/ux/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { normalizePermissions, togglePermission, countPermissions } from '@/constants/permissions';

/* ── placeholder copy ────────────────────────────────────────────────────── */

const DESCRIPTION_PLACEHOLDER = {
    round:         'What happens in this round, and who runs it.\ne.g. First technical screen with the hiring team — 45 minutes, focused on fundamentals.',
    difficulty:    'What this level should feel like to a candidate.\ne.g. Advanced — expects production experience and trade-off reasoning.',
    interviewType: 'What this interview is meant to assess.\ne.g. Hands-on coding under time pressure, judged on correctness and clarity.',
    role:          'What someone in this role is allowed to do.\ne.g. Manages the recruitment pipeline and the team, but cannot change billing.',
};

/**
 * Example hints matched to the kind of interview being configured.
 *
 * A single React-flavoured example is unhelpful on a Behavioural or HR type, so
 * the examples follow the entry's name and fall back to something generic.
 */
const HINT_EXAMPLES = [
    { match: /behaviou?r|culture|hr |hr$|screen|communicat/i,
      skills: 'Communication, Ownership, Stakeholder Management',
      topics: 'Handling Conflict, Receiving Feedback, Dealing with Failure' },
    { match: /coding|dsa|algorithm|program|problem/i,
      skills: 'Data Structures, Algorithms, Problem Solving',
      topics: 'Arrays & Strings, Recursion, Time Complexity' },
    { match: /system|design|architect/i,
      skills: 'System Design, Scalability, Databases',
      topics: 'Caching, Load Balancing, Data Modelling' },
    { match: /leader|manage|director|principal/i,
      skills: 'People Management, Prioritisation, Decision Making',
      topics: 'Building a Team, Handling Underperformance, Setting Strategy' },
];

const DEFAULT_HINTS = {
    skills: 'React, Node.js, SQL',
    topics: 'API Design, State Management, Performance',
};

function hintExamplesFor(name) {
    return HINT_EXAMPLES.find(h => h.match.test(String(name || ''))) || DEFAULT_HINTS;
}

/* ── type metadata ───────────────────────────────────────────────────────── */
const TYPE_META = {
    round: {
        label: 'Rounds',
        singular: 'Round',
        description: 'Define interview rounds (e.g. Round 1, Technical Round, HR Round).',
        color: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20',
    },
    difficulty: {
        label: 'Difficulty Levels',
        singular: 'Difficulty Level',
        description: 'Define difficulty levels that control AI question complexity.',
        color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20',
    },
    interviewType: {
        label: 'Interview Types',
        singular: 'Interview Type',
        description: 'Define interview types (Technical, Coding, Behavioural, etc.).',
        color: 'bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/20',
    },
    role: {
        label: 'Roles',
        singular: 'Role',
        description: 'Define custom roles with specific portal permissions. Assign roles when creating team members.',
        color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    },
};

const PRESET_BUTTONS = [
    { key: 'hr_manager', label: 'HR Manager' },
    { key: 'interviewer', label: 'Interviewer' },
    { key: 'ml_engineer', label: 'ML Engineer' },
];

function slugify(str) {
    return str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

/** True when perm.key is auto-included because a selected parent permission implies it. */
function isImpliedPermission(permissions, key, implies) {
    return Object.entries(implies || {}).some(([parent, deps]) =>
        parent !== key
        && (permissions || []).includes(parent)
        && (deps || []).includes(key)
    );
}

/* ── Role permissions editor ─────────────────────────────────────────────── */
function RolePermissionsEditor({ catalog, portalType, permissions, onPortalTypeChange, onPermissionsChange }) {
    if (!catalog) {
        return (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading permissions…
            </div>
        );
    }

    const implies = catalog.implies || {};

    const applyPreset = (presetKey) => {
        const preset = catalog.presets?.[presetKey];
        if (!preset) return;
        onPortalTypeChange(preset.portalType || 'staff');
        onPermissionsChange(normalizePermissions(preset.permissions || [], implies));
    };

    const onToggle = (key) => {
        onPermissionsChange(togglePermission(permissions, key, implies));
    };

    return (
        <div className="space-y-4 rounded-lg border border-border bg-muted/20 p-4">
            <div className="space-y-2">
                <Label>Portal type <span className="text-destructive">*</span></Label>
                <div className="flex flex-wrap gap-2">
                    {[
                        { value: 'staff', label: 'Staff — admin workspace' },
                        { value: 'candidate', label: 'Candidate — interview portal only' },
                    ].map(opt => (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                                onPortalTypeChange(opt.value);
                                if (opt.value === 'candidate') onPermissionsChange([]);
                            }}
                            className={cn(
                                'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                                portalType === opt.value
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground'
                            )}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {portalType === 'staff' && (
                <>
                    <div className="space-y-2">
                        <Label>Quick presets</Label>
                        <div className="flex flex-wrap gap-2">
                            {PRESET_BUTTONS.map(p => (
                                <Button key={p.key} type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => applyPreset(p.key)}>
                                    {p.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Two columns so all 20 permissions fit without the modal
                        needing its own inner scroll area. */}
                    <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                        {(catalog.groups || []).map(group => (
                            <div key={group.id} className="space-y-2">
                                <p className="text-xs font-semibold text-foreground">{group.label}</p>
                                <div className="space-y-1.5">
                                    {group.permissions.map(perm => {
                                        const checked = (permissions || []).includes(perm.key);
                                        const implied = checked && isImpliedPermission(permissions, perm.key, implies);
                                        return (
                                            <label
                                                key={perm.key}
                                                className={cn(
                                                    'flex items-start gap-2.5 rounded-md border px-3 py-2 transition-colors',
                                                    implied ? 'cursor-default opacity-80' : 'cursor-pointer',
                                                    checked ? 'border-primary/40 bg-primary/5' : 'border-border bg-background hover:bg-muted/40'
                                                )}
                                                title={implied ? 'Included automatically with a related permission' : undefined}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="mt-0.5 h-4 w-4 rounded border-input"
                                                    checked={checked}
                                                    disabled={implied}
                                                    onChange={() => !implied && onToggle(perm.key)}
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-xs font-medium text-foreground">{perm.label}</span>
                                                    {perm.description && (
                                                        <span className="block text-[11px] text-muted-foreground leading-snug">{perm.description}</span>
                                                    )}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                        {countPermissions(permissions)} permission{countPermissions(permissions) === 1 ? '' : 's'} selected.
                        Related permissions are added automatically (e.g. manage includes view).
                    </p>
                </>
            )}

            {portalType === 'candidate' && (
                <p className="text-xs text-muted-foreground">
                    Candidate roles use the interview portal only — no staff permissions apply.
                </p>
            )}
        </div>
    );
}

/* ── Master Form Dialog ──────────────────────────────────────────────────── */
function MasterDialog({ open, onClose, onSave, initial, type, catalog }) {
    const [name, setName]                       = useState('');
    const [slug, setSlug]                       = useState('');
    const [description, setDescription]         = useState('');
    const [portalType, setPortalType]           = useState('staff');
    const [permissions, setPermissions]         = useState([]);
    const [requiredSkills, setRequiredSkills]   = useState([]);
    const [topicAreas, setTopicAreas]           = useState([]);
    const [slugEdited, setSlugEdited]           = useState(false);
    const [saving, setSaving]                   = useState(false);
    const [formError, setFormError]             = useState('');

    const isRole        = type === 'role';
    const isInterviewType = type === 'interviewType';
    // Follows what is typed, so the examples update as you name the type.
    const hints = hintExamplesFor(name || initial?.name);
    const implies = catalog?.implies || {};

    useEffect(() => {
        if (open) {
            setName(initial?.name || '');
            setSlug(initial?.slug || '');
            setDescription(initial?.description || '');
            const meta = initial?.metadata || {};
            const pt = meta.portalType || (meta.portalAccess === 'candidate' ? 'candidate' : 'staff');
            setPortalType(pt);
            setPermissions(normalizePermissions(meta.permissions || [], implies));
            setRequiredSkills(initial?.requiredSkills || []);
            setTopicAreas(initial?.topicAreas || []);
            setSlugEdited(!!initial);
            setSaving(false);
            setFormError('');
        }
    }, [open, initial, implies]);

    const handleNameChange = (v) => {
        setName(v);
        if (!slugEdited) setSlug(slugify(v));
        if (formError) setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) { setFormError('Name is required.'); return; }
        if (isRole && !slug.trim()) { setFormError('Slug is required.'); return; }
        if (isRole && portalType === 'staff' && permissions.length === 0) {
            setFormError('Select at least one permission for a staff role.');
            return;
        }

        setSaving(true);
        setFormError('');
        try {
            // Slug is derived server-side for everything but roles, and sort
            // order comes from dragging — neither is asked for here any more.
            const payload = {
                type,
                name:        name.trim(),
                description: description.trim(),
            };
            if (isRole && !initial) payload.slug = slug.trim();
            if (isRole) {
                payload.metadata = {
                    portalType,
                    permissions: portalType === 'candidate' ? [] : normalizePermissions(permissions, implies),
                };
            }
            // Include skill/topic arrays for interviewType entries
            if (isInterviewType) {
                payload.requiredSkills = requiredSkills;
                payload.topicAreas     = topicAreas;
            }
            await onSave(payload);
            onClose();
        } catch (err) {
            const msg = err.message || 'Failed to save.';
            setFormError(msg);
        } finally {
            setSaving(false);
        }
    };

    const isEdit = !!initial;
    const meta = TYPE_META[type] || {};

    const namePlaceholder =
        type === 'round'      ? 'e.g. Round 1'       :
        type === 'difficulty' ? 'e.g. Hard'           :
        type === 'role'       ? 'e.g. Senior Recruiter' :
                                'e.g. Technical';

    return (
        <Dialog open={open} onOpenChange={v => !v && onClose()}>
            <DialogContent
                className={cn(
                    // Header and actions stay put; only the fields scroll, and
                    // without scrollbar chrome. Never a scrollbar in a scrollbar.
                    'flex max-h-[90vh] flex-col gap-0 overflow-hidden',
                    isRole || isInterviewType ? 'max-w-3xl' : 'max-w-md',
                )}
            >
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit' : 'Add'} {meta.singular}</DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? 'Update the details for this entry.'
                            : `Add a new ${meta.singular?.toLowerCase()} to your company's master list.`}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
                  <div className="no-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto pt-1">
                    {/* Inline form error banner */}
                    {formError && (
                        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5">
                            <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
                            <p className="text-sm text-destructive leading-snug">{formError}</p>
                        </div>
                    )}

                    <div className="space-y-1.5">
                        <Label htmlFor="m-name">Name <span className="text-destructive">*</span></Label>
                        <Input
                            id="m-name"
                            value={name}
                            onChange={e => handleNameChange(e.target.value)}
                            placeholder={namePlaceholder}
                        />
                    </div>

                    {isRole && (
                        <RolePermissionsEditor
                            catalog={catalog}
                            portalType={portalType}
                            permissions={permissions}
                            onPortalTypeChange={setPortalType}
                            onPermissionsChange={setPermissions}
                        />
                    )}

                    {/* Roles keep a visible slug: it is the identifier users are
                        assigned by. It is fixed once the role exists, because
                        changing it would strand every user holding it. */}
                    {isRole && (
                        <div className="space-y-1.5">
                            <Label htmlFor="m-slug">
                                Slug {!initial && <span className="text-destructive">*</span>}
                            </Label>
                            <Input
                                id="m-slug"
                                value={slug}
                                readOnly={!!initial}
                                disabled={!!initial}
                                onChange={e => { setSlug(e.target.value); setSlugEdited(true); if (formError) setFormError(''); }}
                                placeholder="e.g. senior_recruiter"
                                className={cn(initial && 'bg-muted text-muted-foreground cursor-not-allowed')}
                            />
                            <p className="text-xs text-muted-foreground">
                                {initial
                                    ? 'Fixed — team members are assigned by this. Rename the role above instead.'
                                    : 'Used as the role identifier when creating users.'}
                            </p>
                        </div>
                    )}


                    <div className="space-y-1.5">
                        <Label htmlFor="m-desc">Description</Label>
                        <Textarea
                            id="m-desc"
                            rows={3}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={DESCRIPTION_PLACEHOLDER[type] || 'Optional — what this is for'}
                            className="resize-y text-sm"
                        />
                    </div>

                    {/* ── AI question-generation hints (interview type only) ── */}
                    {isInterviewType && (
                        <div className="space-y-4 rounded-lg border border-violet-500/20 bg-violet-500/5 p-4">
                            <div className="flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-violet-500" />
                                <p className="text-sm font-semibold text-foreground">AI Question Generation Hints</p>
                            </div>
                            <p className="text-xs text-muted-foreground -mt-2">
                                These settings guide the AI when generating interview questions for this type.
                                Leave empty to rely on auto-detection from the job description.
                            </p>

                            <div className="space-y-1.5">
                                <Label>Required Skills</Label>
                                <TagInput
                                    value={requiredSkills}
                                    onChange={setRequiredSkills}
                                    placeholder={`Type a skill, press Enter — e.g. ${hints.skills}`}
                                    disabled={saving}
                                />
                                <p className="text-xs text-muted-foreground">
                                    AI will guarantee at least one question tests each skill listed here.
                                    These take priority over skills auto-detected from the job description.
                                </p>
                            </div>

                            <div className="space-y-1.5">
                                <Label>Focus Topic Areas</Label>
                                <TagInput
                                    value={topicAreas}
                                    onChange={setTopicAreas}
                                    placeholder={`Type a topic, press Enter — e.g. ${hints.topics}`}
                                    disabled={saving}
                                />
                                <p className="text-xs text-muted-foreground">
                                    AI will prioritise questions from these topic areas when generating the question set.
                                </p>
                            </div>
                        </div>
                    )}

                  </div>

                    <DialogFooter className="mt-4 shrink-0 border-t border-border pt-4">
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                        <Button type="submit" disabled={saving}>
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {isEdit ? 'Save Changes' : 'Add'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

const PORTAL_TYPE_LABELS = {
    staff:     { label: 'Staff',     color: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20' },
    candidate: { label: 'Candidate', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/20' },
};

/* ── Master Row ──────────────────────────────────────────────────────────── */
function MasterRow({ master, type, onEdit, onToggle, toggling, draggable, dragHandlers, isDragging, isDropTarget }) {
    const active = master.isActive;
    const meta   = TYPE_META[type];
    const roleMeta = master.metadata || {};
    const portalType = roleMeta.portalType || (roleMeta.portalAccess === 'candidate' ? 'candidate' : 'staff');
    const ptLabel = PORTAL_TYPE_LABELS[portalType];
    const permCount = (roleMeta.permissions || []).length;

    return (
        <div
            draggable={draggable}
            {...(draggable ? dragHandlers : {})}
            className={cn(
                'flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors group',
                active
                    ? 'bg-card hover:bg-muted/40 border-border'
                    : 'bg-muted/20 border-dashed border-border/60 opacity-70',
                draggable && 'cursor-grab active:cursor-grabbing',
                isDragging && 'opacity-40',
                isDropTarget && 'border-primary ring-1 ring-primary',
            )}
        >
            <GripVertical className={cn('h-4 w-4 shrink-0',
                draggable ? 'text-muted-foreground/70' : 'text-muted-foreground/40')} />

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={cn(
                        'font-medium text-sm',
                        !active && 'line-through text-muted-foreground',
                    )}>
                        {master.name}
                    </span>
                    {/* Slug is internal for everything but roles, where it is the
                        identifier users are assigned by. */}
                    {type === 'role' && (
                        <Badge variant="outline" className={cn('text-xs font-mono', meta?.color)}>
                            {master.slug}
                        </Badge>
                    )}
                    {/* Portal type + permission count — role type only */}
                    {type === 'role' && ptLabel && (
                        <>
                            <span className={cn(
                                'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                                ptLabel.color
                            )}>
                                {ptLabel.label}
                            </span>
                            {portalType === 'staff' && permCount > 0 && (
                                <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    {permCount} permission{permCount === 1 ? '' : 's'}
                                </span>
                            )}
                        </>
                    )}
                    {/* Active / Inactive status badge */}
                    {active ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                            <CheckCircle2 className="h-2.5 w-2.5" />Active
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-muted-foreground/20 bg-muted/40 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                            <XCircle className="h-2.5 w-2.5" />Inactive
                        </span>
                    )}
                </div>
                {master.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{master.description}</p>
                )}
                {/* Required skills preview — interviewType only */}
                {type === 'interviewType' && (master.requiredSkills?.length > 0 || master.topicAreas?.length > 0) && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                        {master.requiredSkills?.slice(0, 5).map(skill => (
                            <span key={skill} className="inline-flex items-center rounded-md border border-violet-500/25 bg-violet-500/8 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:text-violet-400">
                                {skill}
                            </span>
                        ))}
                        {master.requiredSkills?.length > 5 && (
                            <span className="text-[10px] text-muted-foreground">+{master.requiredSkills.length - 5} more skills</span>
                        )}
                        {master.topicAreas?.slice(0, 3).map(area => (
                            <span key={area} className="inline-flex items-center rounded-md border border-sky-500/25 bg-sky-500/8 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-400">
                                {area}
                            </span>
                        ))}
                        {master.topicAreas?.length > 3 && (
                            <span className="text-[10px] text-muted-foreground">+{master.topicAreas.length - 3} more topics</span>
                        )}
                    </div>
                )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
                {/* Edit — only for active items */}
                {active && (
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit"
                        onClick={() => onEdit(master)}
                    >
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                )}

                {/* Toggle active/inactive */}
                <Button
                    size="sm"
                    variant="ghost"
                    className={cn(
                        'h-7 gap-1.5 px-2 text-xs font-medium transition-colors',
                        active
                            ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
                            : 'text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10',
                    )}
                    title={active ? 'Deactivate' : 'Activate'}
                    disabled={toggling === master._id}
                    onClick={() => onToggle(master)}
                >
                    {toggling === master._id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : active
                            ? <ToggleRight className="h-4 w-4" />
                            : <ToggleLeft  className="h-4 w-4" />
                    }
                    <span className="hidden sm:inline">{active ? 'Disable' : 'Enable'}</span>
                </Button>
            </div>
        </div>
    );
}

/* ── Tab Panel ───────────────────────────────────────────────────────────── */
function MasterPanel({ type, masters, loading, onAdd, onEdit, onToggle, toggling, onReorder }) {
    const meta     = TYPE_META[type];
    const inactive = masters.filter(m => !m.isActive);

    // Dragging is applied optimistically so the list follows the cursor; the
    // server order is authoritative once the drop is persisted.
    const [localActive, setLocalActive] = useState(() => masters.filter(m => m.isActive));
    const [dragId, setDragId]   = useState(null);
    const [overId, setOverId]   = useState(null);
    const [saving, setSaving]   = useState(false);

    useEffect(() => { setLocalActive(masters.filter(m => m.isActive)); }, [masters]);

    const active = localActive;

    const handleDrop = async (targetId) => {
        const from = active.findIndex(m => m._id === dragId);
        const to   = active.findIndex(m => m._id === targetId);
        setDragId(null); setOverId(null);
        if (from === -1 || to === -1 || from === to) return;

        const next = [...active];
        next.splice(to, 0, next.splice(from, 1)[0]);
        setLocalActive(next);

        // Inactive entries keep trailing positions so the whole type stays a
        // clean 1..n and no two rows share an order.
        setSaving(true);
        try {
            await onReorder(type, [...next, ...inactive].map(m => m._id));
        } catch (err) {
            setLocalActive(masters.filter(m => m.isActive));   // put it back
            toast.error(err.message || 'Could not save the new order.');
        } finally {
            setSaving(false);
        }
    };

    const dragHandlersFor = (m) => ({
        onDragStart: (e) => { setDragId(m._id); e.dataTransfer.effectAllowed = 'move'; },
        onDragOver:  (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overId !== m._id) setOverId(m._id); },
        onDragLeave: () => setOverId(prev => (prev === m._id ? null : prev)),
        onDrop:      (e) => { e.preventDefault(); handleDrop(m._id); },
        onDragEnd:   () => { setDragId(null); setOverId(null); },
    });

    return (
        <div className="space-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm text-muted-foreground">{meta.description}</p>
                    {masters.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                            <span className="font-semibold text-emerald-700 dark:text-emerald-400">{active.length} active</span>
                            {inactive.length > 0 && (
                                <span className="ml-1">· {inactive.length} inactive</span>
                            )}
                            <span className="ml-1">of {masters.length} total</span>
                        </p>
                    )}
                </div>
                <Button size="sm" onClick={onAdd}>
                    <Plus className="mr-1.5 h-4 w-4" /> Add {meta.singular}
                </Button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : masters.length === 0 ? (
                <div className="rounded-lg border border-dashed py-12 text-center text-muted-foreground text-sm">
                    No {meta.label.toLowerCase()} yet.{' '}
                    <button className="text-primary underline underline-offset-2" onClick={onAdd}>
                        Add one to get started.
                    </button>
                </div>
            ) : (
                <div className="space-y-2">
                    {active.length > 1 && (
                        <p className="flex items-center gap-1.5 pb-0.5 text-xs text-muted-foreground">
                            <GripVertical className="h-3 w-3" />
                            Drag to reorder — this is the order they appear in dropdowns.
                            {saving && <Loader2 className="ml-1 h-3 w-3 animate-spin" />}
                        </p>
                    )}

                    {/* Active items first, and the only ones that are sortable */}
                    {active.map(m => (
                        <MasterRow
                            key={m._id} master={m} type={type}
                            onEdit={onEdit} onToggle={onToggle} toggling={toggling}
                            draggable={active.length > 1}
                            dragHandlers={dragHandlersFor(m)}
                            isDragging={dragId === m._id}
                            isDropTarget={overId === m._id && dragId !== m._id}
                        />
                    ))}

                    {/* Inactive separator */}
                    {inactive.length > 0 && (
                        <>
                            {active.length > 0 && (
                                <div className="flex items-center gap-2 py-1">
                                    <div className="flex-1 h-px bg-border" />
                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                                        Inactive / Disabled
                                    </span>
                                    <div className="flex-1 h-px bg-border" />
                                </div>
                            )}
                            {inactive.map(m => (
                                <MasterRow key={m._id} master={m} type={type} onEdit={onEdit} onToggle={onToggle} toggling={toggling} />
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* ── Main Page ───────────────────────────────────────────────────────────── */
export default function InterviewMasterPage() {
    const { user } = useAuth();
    const [data, setData]           = useState({ round: [], difficulty: [], interviewType: [], role: [] });
    const [catalog, setCatalog]       = useState(null);
    const [loading, setLoading]     = useState(true);
    const [activeTab, setActiveTab] = useState('round');

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing]       = useState(null); // null = create mode

    // Toggle loading state — stores the _id being toggled
    const [toggling, setToggling] = useState('');

    const fetchMasters = useCallback(async () => {
        setLoading(true);
        try {
            const [mastersRes, catalogRes] = await Promise.all([
                apiClient.getMasters(),
                apiClient.getPermissionsCatalog().catch(() => null),
            ]);
            setData(mastersRes.data || { round: [], difficulty: [], interviewType: [], role: [] });
            if (catalogRes?.data) setCatalog(catalogRes.data);
        } catch (err) {
            toast.error(err.message || 'Failed to load masters. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMasters(); }, [fetchMasters]);

    // Save (create or update) — errors bubble up to MasterDialog for inline display
    const handleSave = async (payload) => {
        if (editing) {
            const res = await apiClient.updateMaster(editing._id, payload);
            toast.success(res.message || 'Updated successfully.');
        } else {
            const res = await apiClient.createMaster(payload);
            toast.success(res.message || 'Added successfully.');
        }
        await fetchMasters();
    };

    // Toggle active/inactive
    const handleToggle = async (master) => {
        setToggling(master._id);
        try {
            const res = await apiClient.toggleMaster(master._id);
            toast.success(res.message || `"${master.name}" ${master.isActive ? 'deactivated' : 'activated'}.`);
            // Optimistic local update for instant feedback
            setData(prev => {
                const updated = { ...prev };
                ['round', 'difficulty', 'interviewType', 'role'].forEach(t => {
                    if (updated[t]) {
                        updated[t] = updated[t].map(m =>
                            m._id === master._id ? { ...m, isActive: !m.isActive } : m
                        );
                    }
                });
                return updated;
            });
        } catch (err) {
            toast.error(err.message || 'Failed to update status.');
        } finally {
            setToggling('');
        }
    };

    // The panel updates itself optimistically, so only the persisted result is
    // merged back — a failure re-throws and the panel restores its old order.
    const handleReorder = async (type, orderedIds) => {
        const res = await apiClient.reorderMasters(type, orderedIds);
        setData(prev => ({ ...prev, [type]: res.data || prev[type] }));
    };

    const openAdd  = ()       => { setEditing(null);   setDialogOpen(true); };
    const openEdit = (master) => { setEditing(master); setDialogOpen(true); };

    // Tab labels with active counts
    const tabLabel = (type) => {
        const items  = data[type] || [];
        const active = items.filter(m => m.isActive).length;
        return `${TYPE_META[type].label} (${active})`;
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Interview Masters"
                description="Configure your company's rounds, difficulty levels, and interview types. Changes apply only to your company."
                actions={
                    <div className="flex items-center gap-2">
                        {user?.companyId && (
                            <span className="hidden sm:flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-3 py-1 text-xs text-muted-foreground">
                                <Building2 className="h-3 w-3 shrink-0" />
                                <span className="font-semibold text-foreground">{user.companyId}</span>
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={fetchMasters} disabled={loading}>
                            <RotateCcw className={cn('mr-1.5 h-4 w-4', loading && 'animate-spin')} />
                            Refresh
                        </Button>
                    </div>
                }
            />

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="w-full sm:w-auto">
                    {['round', 'difficulty', 'interviewType', 'role'].map(t => (
                        <TabsTrigger key={t} value={t}>{tabLabel(t)}</TabsTrigger>
                    ))}
                </TabsList>

                {['round', 'difficulty', 'interviewType', 'role'].map(type => (
                    <TabsContent key={type} value={type} className="mt-4">
                        <MasterPanel
                            type={type}
                            masters={data[type] || []}
                            loading={loading}
                            onAdd={openAdd}
                            onEdit={openEdit}
                            onToggle={handleToggle}
                            toggling={toggling}
                            onReorder={handleReorder}
                        />
                    </TabsContent>
                ))}
            </Tabs>

            <MasterDialog
                open={dialogOpen}
                onClose={() => { setDialogOpen(false); setEditing(null); }}
                onSave={handleSave}
                initial={editing}
                type={activeTab}
                catalog={catalog}
            />
        </div>
    );
}
